/**
 * Filter Operators Utility
 * Provides filter operators based on field type
 */

export type FilterOperator =
    | 'eq'
    | 'neq'
    | 'lt'
    | 'lte'
    | 'gt'
    | 'gte'
    | 'contains'
    | 'ncontains'
    | 'icontains'
    | 'starts_with'
    | 'nstarts_with'
    | 'istarts_with'
    | 'nistarts_with'
    | 'ends_with'
    | 'nends_with'
    | 'iends_with'
    | 'niends_with'
    | 'in'
    | 'nin'
    | 'between'
    | 'nbetween'
    | 'empty'
    | 'nempty'
    | 'null'
    | 'nnull';

export type FieldType =
    | 'string'
    | 'text'
    | 'boolean'
    | 'integer'
    | 'bigInteger'
    | 'float'
    | 'decimal'
    | 'dateTime'
    | 'date'
    | 'time'
    | 'timestamp'
    | 'json'
    | 'uuid'
    | 'csv'
    | 'hash'
    | 'binary'
    | 'geometry'
    | 'alias'
    | 'unknown';

// Operator labels for display
export const operatorLabels: Record<FilterOperator, string> = {
    eq: 'equals',
    neq: 'not equals',
    lt: 'less than',
    lte: 'less than or equal to',
    gt: 'greater than',
    gte: 'greater than or equal to',
    contains: 'contains',
    ncontains: 'doesn\'t contain',
    icontains: 'contains (case-insensitive)',
    starts_with: 'starts with',
    nstarts_with: 'doesn\'t start with',
    istarts_with: 'starts with (case-insensitive)',
    nistarts_with: 'doesn\'t start with (case-insensitive)',
    ends_with: 'ends with',
    nends_with: 'doesn\'t end with',
    iends_with: 'ends with (case-insensitive)',
    niends_with: 'doesn\'t end with (case-insensitive)',
    in: 'is one of',
    nin: 'is not one of',
    between: 'is between',
    nbetween: 'is not between',
    empty: 'is empty',
    nempty: 'is not empty',
    null: 'is null',
    nnull: 'is not null',
};

// Boolean operators that don't need a value input
export const booleanOperators: FilterOperator[] = ['empty', 'nempty', 'null', 'nnull'];

// Operators that need array input
export const arrayOperators: FilterOperator[] = ['in', 'nin', 'between', 'nbetween'];

/**
 * Get filter operators available for a specific field type
 */
export function getFilterOperatorsForType(type: string): FilterOperator[] {
    const normalizedType = type?.toLowerCase() || 'string';

    switch (normalizedType) {
        // Text types
        case 'binary':
        case 'string':
        case 'text':
        case 'csv':
            return [
                'contains',
                'ncontains',
                'icontains',
                'starts_with',
                'nstarts_with',
                'istarts_with',
                'nistarts_with',
                'ends_with',
                'nends_with',
                'iends_with',
                'niends_with',
                'eq',
                'neq',
                'empty',
                'nempty',
                'null',
                'nnull',
                'in',
                'nin',
            ];

        case 'hash':
            return ['empty', 'nempty', 'null', 'nnull'];

        case 'uuid':
            return ['eq', 'neq', 'null', 'nnull', 'in', 'nin'];

        case 'json':
            return ['null', 'nnull'];

        case 'boolean':
            return ['eq', 'neq', 'null', 'nnull'];

        // Number types
        case 'biginteger':
        case 'integer':
        case 'decimal':
        case 'float':
            return ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'between', 'nbetween', 'null', 'nnull', 'in', 'nin'];

        // Date/time types
        case 'datetime':
        case 'date':
        case 'time':
        case 'timestamp':
            return ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'between', 'nbetween', 'null', 'nnull', 'in', 'nin'];

        case 'geometry':
            return ['eq', 'neq', 'null', 'nnull'];

        // Default for unknown types
        default:
            return [
                'contains',
                'ncontains',
                'eq',
                'neq',
                'lt',
                'lte',
                'gt',
                'gte',
                'between',
                'nbetween',
                'empty',
                'nempty',
                'null',
                'nnull',
                'in',
                'nin',
            ];
    }
}

/**
 * Get the default operator for a field type
 */
export function getDefaultOperator(type: string): FilterOperator {
    const operators = getFilterOperatorsForType(type);

    // Prefer 'eq' if available, otherwise use first operator
    if (operators.includes('eq')) {
        return 'eq';
    }

    // Fallback to 'eq' if no operators (shouldn't happen)
    return operators[0] ?? 'eq';
}

/**
 * Check if operator requires a value input
 */
export function operatorNeedsValue(operator: FilterOperator): boolean {
    return !booleanOperators.includes(operator);
}

/**
 * Check if operator expects an array value
 */
export function operatorExpectsArray(operator: FilterOperator): boolean {
    return arrayOperators.includes(operator);
}

/**
 * Get the input type for a field type
 */
export function getInputTypeForFieldType(fieldType: string): 'text' | 'number' | 'date' | 'datetime' | 'boolean' | 'select' {
    const normalizedType = fieldType?.toLowerCase() || 'string';

    switch (normalizedType) {
        case 'boolean':
            return 'boolean';
        case 'integer':
        case 'biginteger':
        case 'float':
        case 'decimal':
            return 'number';
        case 'date':
            return 'date';
        case 'datetime':
        case 'timestamp':
        case 'time':
            return 'datetime';
        default:
            return 'text';
    }
}
